import { useTranslation } from 'react-i18next';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { DoctorProfileUseForm } from './use-doctor-profile-form';

const TRIGGER_CLASSNAME = 'w-[120px]';

/**
 * A two-value select (thesis / installable) laid out label-left, value-right,
 * as the profile forms render boolean choices.
 *
 * @param props.form - the unified profile form
 * @param props.fieldName - the bound form field
 * @param props.label - i18n key of the label
 * @param props.items - the selectable values with their i18n labels
 * @returns the form-bound select
 */
export const BooleanSelectField = ({
  form,
  fieldName,
  label,
  items,
}: {
  form: DoctorProfileUseForm;
  fieldName: 'thesis' | 'installable';
  label: string;
  items: readonly { id: string; value: string; label: string }[];
}) => {
  const { t } = useTranslation();

  return (
    <FormField
      control={form.control}
      name={fieldName}
      render={({ field }) => (
        <FormItem>
          <div className="flex flex-row items-center justify-between gap-2 space-y-0">
            <FormLabel>{t(label)}</FormLabel>

            <Select
              onValueChange={field.onChange}
              value={field.value ?? undefined}
            >
              <FormControl>
                <SelectTrigger className={TRIGGER_CLASSNAME}>
                  <SelectValue />
                </SelectTrigger>
              </FormControl>

              <SelectContent side="top">
                {items.map((item) => (
                  <SelectItem
                    key={item.id}
                    value={item.value}
                  >
                    {t(item.label)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <FormMessage />
        </FormItem>
      )}
    />
  );
};
